import { CircleAlertIcon as IconAlertCircle } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom-v5-compat';

import CasesSummary from 'dashboard/components/CasesSummary';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ReportDetails from 'dashboard/components/ReportDetails';
import ReportHeader from 'dashboard/components/ReportDetails/ReportHeader';
import TagsSummary from 'dashboard/components/TagsSummary';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FormReportFlagIssuesData } from 'dashboard/forms/FormReportFlagIssues';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import {
  Backdrop,
  ColumnContainer,
  Container,
  HeaderContainer,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { Case } from 'dashboard/models/case';
import { RelatedReport, Report, REPORT_TYPE_TO_LABEL } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import ReportInfo from 'dashboard/views/DashboardReportsShowView/ReportInfo';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';

interface DashboardReportsShowViewProps {
  report: Report;
  reportTemplate: ReportTemplate;
  reportTags: Tag[];
  cases: Case[];
  users: User[];
  relatedReports: RelatedReport[];
  featureFlags: FeatureFlagsMap;
  isLoading: boolean;
  handlePrintReport: () => Promise<void>;
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
  handleFlagIssues: (data: FormReportFlagIssuesData) => Promise<void>;
  handleRunReportAdhoc: () => Promise<void>;
  handlePauseReport: () => Promise<void>;
  handleResumeReport: () => Promise<void>;
  handleRedactReport: () => Promise<void>;
}

const DashboardReportsShowView = ({
  report,
  reportTemplate,
  reportTags,
  cases,
  users,
  relatedReports,
  isLoading,
  handlePrintReport,
  handleUpdateTags,
  handleFlagIssues,
  handleRunReportAdhoc,
  handlePauseReport,
  handleResumeReport,
  handleRedactReport,
}: DashboardReportsShowViewProps) => {
  const hasReportWritePermission = usePermission(Permission.ReportWrite);

  if (report == null) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState message='We couldn’t find that Report.' backRoute='/dashboard/reports' />
      </Backdrop>
    );
  }

  let redactionBannerText = null;
  if (report.attributes['redacted-at'] != null) {
    redactionBannerText = `This report was redacted on ${DateTime.fromISO(
      report.attributes['redacted-at'],
    ).toFormat(DATE_AND_TIME_FORMAT)}.`;
  } else if (report.attributes['parent-marked-for-cascading-redaction-at'] != null) {
    redactionBannerText = `This report was marked for redaction on ${DateTime.fromISO(
      report.attributes['parent-marked-for-cascading-redaction-at'],
    ).toFormat(DATE_AND_TIME_FORMAT)}.`;
  }

  return (
    <>
      <Helmet title={`${report.id} - Reports`} />
      <InactiveEnvironmentBanner />
      {redactionBannerText && (
        <Banner data-test='report-redaction-banner' icon={<IconAlertCircle />}>
          {redactionBannerText}
        </Banner>
      )}

      <Backdrop>
        <Container>
          <HeaderContainer>
            <ReportHeader
              report={report}
              handlePrintReport={handlePrintReport}
              handleFlagIssues={handleFlagIssues}
              handleRunReportAdhoc={handleRunReportAdhoc}
              handlePauseReport={handlePauseReport}
              handleResumeReport={handleResumeReport}
              handleRedactReport={handleRedactReport}
            />
          </HeaderContainer>
          <ColumnContainer>
            <MainColumn>
              <ReportDetails report={report} reportTemplate={reportTemplate} />
            </MainColumn>
            <RightColumn>
              <ReportInfo report={report} reportTemplate={reportTemplate} />
              {relatedReports.length > 0 && (
                <Card>
                  <Card.Body>
                    <Card.Title>Related reports</Card.Title>
                    {relatedReports.map((report) => (
                      <Link to={`/dashboard/reports/${report.token}`} key={report.token}>{`${
                        REPORT_TYPE_TO_LABEL[report.type]
                      }`}</Link>
                    ))}
                  </Card.Body>
                </Card>
              )}
              <TagsSummary
                tags={reportTags}
                tagScope={ApiModel.Report}
                canEdit={hasReportWritePermission}
                handleUpdateTags={handleUpdateTags}
              />
              <CasesSummary
                cases={cases}
                users={users}
                nullStateText='There are no cases associated with this report.'
              />
            </RightColumn>
          </ColumnContainer>
        </Container>
      </Backdrop>
    </>
  );
};

export default DashboardReportsShowView;
